import { useSyncExternalStore } from 'react';
import { getListingKey } from './listingDetailsApi.js';

// Listings the user has opened in the full view, kept in this browser (localStorage) so the map markers can
// show which homes have already been looked at. Only the listing's key is stored (not a snapshot): "visited"
// is just "opened before". Shared by kartvy and listvy; tabs stay in sync through the storage event. A plain
// cookie would be a poor fit here (sent to the server on every request and size-limited), so it lives in
// localStorage like the favourites (listing_favorites_v1).

const STORAGE_KEY = 'listing_visited_v1';
// Keep the most recently opened this many; the set is insertion-ordered so the oldest drop off.
const MAX_VISITED = 4000;

const listeners = new Set();
let visited = null; // Set of listing keys
let version = 0;

export const getVisitedKey = (listing) => getListingKey(listing) || '';

const readStorage = () => {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
        return new Set(Array.isArray(parsed) ? parsed.filter((key) => typeof key === 'string' && key) : []);
    } catch (error) {
        return new Set();
    }
};

const getVisited = () => {
    if (visited == null) visited = readStorage();
    return visited;
};

const persist = () => {
    try {
        const keys = [...visited];
        if (keys.length > MAX_VISITED) {
            visited = new Set(keys.slice(keys.length - MAX_VISITED));
        }
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...visited]));
    } catch (error) {
        // Storage full or blocked (private mode): visited still works until the page is closed.
    }
};

export const isVisitedKey = (key) => Boolean(key) && getVisited().has(key);
export const isVisitedListing = (listing) => isVisitedKey(getVisitedKey(listing));

// Marks a listing as opened. Does nothing if already visited, so a re-open never triggers a re-render.
export const markVisited = (listing) => {
    const key = getVisitedKey(listing);
    if (!key || getVisited().has(key)) return;
    getVisited().add(key);
    persist();
    version += 1;
    listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
    listeners.add(listener);
    const handleStorage = (event) => {
        if (event.key !== STORAGE_KEY) return;
        visited = readStorage();
        version += 1;
        listener();
    };
    window.addEventListener('storage', handleStorage);
    return () => {
        listeners.delete(listener);
        window.removeEventListener('storage', handleStorage);
    };
};

const getVersion = () => version;

// A number that changes whenever the visited set changes; use it in a marker effect's deps (and read the
// current set with isVisitedKey inside) so the markers re-render when a listing is opened.
export const useVisitedVersion = () => useSyncExternalStore(subscribe, getVersion, () => 0);
